import axios, { AxiosInstance } from 'axios'
import objectToFormData from '../helpers/objectToFormData'
import { toSnakeCase } from '../helpers/stringHelper'

export enum ApiMethods {
    GET = 'get',
    POST = 'post',
    PUT = 'put',
    DELETE = 'delete',
}

export enum ApiRoutes {
    LOGIN = 'auth/login',
    CHECK_AUTH = 'auth/check',
    LOGOUT = 'auth/logout',
    CART = 'cart',
    PLUGIN = 'plugin',
    TYPES_PUBLIC = 'types/public',
    TYPES = 'types',
    CATEGORIES = 'categories',
    CATEGORIES_SHOP = 'categories/shop',
    ARTICLES = 'articles',
    HERSTELLER = 'hersteller',
    MERKMALE = 'merkmale',
    ONBOARDING_URL = 'onboarding-url',
    ONBOARDING_SUBMIT = 'onboarding/submit',
    EMAILS = 'emails',
    ONBOARDING_CUSTOMERS = 'onboarding/customers',
    COLORS = 'colors',
    FONTS = 'fonts',
}

const plugin_id: string | undefined = process.env.REACT_APP_PLUGIN_ID

class ApiServiceV2 {
    private instance: AxiosInstance

    private getApiUrl(): string {
        const wrapperId = `${(plugin_id ?? 'plugin').trim().toLowerCase().replace(/\s+/g, '-')}-wrapper`
        const wrapper = document.getElementById(wrapperId)
        const apiUrlId = `${toSnakeCase(plugin_id)}_api_url`
        const apiBaseUrl = localStorage.getItem(apiUrlId)

        let url = '/'
        
        if (wrapper && wrapper.dataset.api) {
            url = wrapper.dataset.api
        } else {
            const frontendContainer = document.getElementById('zr-onboarding-form-frontend')
            
            if (frontendContainer && frontendContainer.dataset.apiUrl) {
                url = frontendContainer.dataset.apiUrl
            } else if (apiBaseUrl) {
                url = apiBaseUrl
            }
        }

        return url
    }

    constructor() {
        const url = this.getApiUrl()
        this.instance = axios.create({
            baseURL: url,
        })
    }

    toUrl(
        route: ApiRoutes,
        id?: string | number,
        params?: Record<string, string>
    ): string {
        let url = id ? (`${route}/${id}` as string) : route
        if (params) {
            const filtered: Record<string, string> = {}
            Object.keys(params).forEach((key) => {
                if (params[key] && params[key] !== '') {
                    filtered[key] = params[key]
                }
            })

            const queryString = new URLSearchParams(filtered).toString()
            if (queryString) {
                url += `?${queryString}`
            }
        }
        return url
    }

    async callApi(
        route: string,
        method = ApiMethods.GET,
        data: object = {},
        files: Blob[] = []
    ) {
        // Update baseURL dynamically in case container was added after ApiService initialization
        const currentUrl = this.getApiUrl()
        if (this.instance.defaults.baseURL !== currentUrl) {
            this.instance.defaults.baseURL = currentUrl
        }

        const tokenKey = `${toSnakeCase(plugin_id)}_token`
        const token = localStorage.getItem(tokenKey)
        if (token) {
            this.instance.defaults.headers.common['Authorization'] =
                `Bearer ${token}`
        }

        return await this.instance.request({
            method: method,
            url: route,
            data: objectToFormData({ ...data, ...files }),
            responseType: 'json',
        })
    }

    async post(
        route: ApiRoutes,
        id?: string | number,
        data: object = {},
        files: Blob[] = []
    ) {
        return await this.callApi(
            this.toUrl(route, id),
            ApiMethods.POST,
            data,
            files
        )
    }

    async get(
        route: ApiRoutes,
        id?: string | number,
        params?: Record<string, string>
    ) {
        return await this.callApi(this.toUrl(route, id, params), ApiMethods.GET)
    }

    async delete(route: ApiRoutes, id?: string | number) {
        return await this.callApi(this.toUrl(route, id), ApiMethods.DELETE)
    }

    async put(
        route: ApiRoutes,
        id?: string | number,
        data: object = {},
        files: Blob[] = []
    ) {
        return await this.callApi(
            this.toUrl(route, id),
            ApiMethods.PUT,
            data,
            files
        )
    }

    getBaseUrl(): string {
        return this.getApiUrl()
    }
}

export const api = new ApiServiceV2()
